import {BookingLink, NotSetBookingLink} from './table-links'
import PaymentStatusIcon from './PaymentStatusIcon'
import {notifyStandardError, postJson, resolveRoute} from '../helpers/ajax'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import type {DashboardExtraOptions, DashboardVehicleOption} from '../dashboard-datatable-options'
import filterStore from '../filter-store'
import type {SelectBoxProps} from './SelectBox'
import SelectBox from './SelectBox'
import type {OptionTuples} from '../helpers/react-helpers'
import {appendPopup, confirmDialogPromise} from '../helpers/react-helpers'
import {notifySuccess} from '../notify'
import type {BookingConflict, BookingConflictsResponse} from './ConflictsDialog'
import ConflictsDialog from './ConflictsDialog'
import {usePropState} from '../hooks/usePropState'
import {ActionButtonIcon} from './SaveSubmitButton'
import icons from '../../markons/markons'
import {PhoneLink} from './external-links'
import Icon from './Icon'
import assert from 'assert'
import css from './BookingsDataTable.less'
import classNames from 'classnames'
import kb from '../kb'
import {assumeDefinedProps, assumeType} from '../types/assert'
import type {DataTableApi, DataTableProps} from './datatable'
import {DataTable} from './datatable'
import {EMPTY_OBJECT} from '../helpers/constants'
import type {DefaultRenderProps, OrderTuple} from './datatable/dt-types'

type Driver = {
    id: number;
    phone: string;
    name: string;
    status: "ASSIGNED" | "UNASSIGNED" | string; // Assuming additional possible statuses
}

type BookingTableRowData = {
    booking_id: number;
    segment_id: number;
    single_vehicle_booking: boolean;
    instant_quote_applied: boolean;
    datetime: string; // Contains HTML as a string
    date: string;
    /** Pickup day of the week */
    pickup_dow: string;
    time: string; // Contains HTML as a string
    passenger: {
        sameAsCustomer: boolean;
        name: string
    }
    client: {
        name: string;
        phone: string;
        phone_type: "CELL" | "HOME" | "WORK" | string; // Assuming additional possible values
    };
    driver: Driver | null;
    booking_number: number;
    vehicle: {
        id: number;
        style: {
            backgroundColor: string;
            color: string;
        };
        name: string;
        code: string;
    };
    pickup_place: string;
    pickup_city: string;
    dropoff_place: string;
    dropoff_city: string;
    creator: string;
    create_date: string;
    past: boolean;
    currency: string;
    bill_total: number;
    pay_total: number;
    status: "confirmed" | "cancelled" | "pending" | string; // Assuming additional possible statuses
    segment_count: number,
    driver_alert: string | null,
};

function sameBooking(a: BookingTableRowData, b: BookingTableRowData) {
    return a.booking_id === b.booking_id
}

function singleVehicleBooking(a: BookingTableRowData, b: BookingTableRowData) {
    return sameBooking(a, b) && !!a.single_vehicle_booking
}

function sameDate(a: BookingTableRowData, b: BookingTableRowData) {
    return sameBooking(a, b) && a.date === b.date
}

const minPlaceWidth = '100px'
const maxPlaceWidth = '200px'
const minCityWidth = '100px'
const maxCityWidth = '150px'
const customerWidth = '200px'
const driverWidth = '100px'
const alertWidth = '130px'
const unitWidth = '86px'
const numberWidth = '65px'

type TableProps = DataTableProps<BookingTableRowData>
type Column = TableProps['columns'][number]

type MinCellData = Pick<BookingTableRowData, 'booking_id'>


export function DefaultBookingCellLink<T extends MinCellData>({data, row, meta, children}: DefaultRenderProps<T>) {
    const style = {
        maxWidth: meta.columnSettings.maxWidth,
        minWidth: meta.columnSettings.minWidth,
        width: meta.columnSettings.width,
    }
    if(!children) {
        return <NotSetBookingLink bookingId={row.booking_id} style={style}>Not Set</NotSetBookingLink>
    }
    if(typeof children === 'string') {
        // TODO: https://developer.mozilla.org/en-US/docs/Web/API/TrustedHTML
        // "Sanitizer API" doesn't exist yet; https://web.dev/articles/sanitizer
        // https://chromestatus.com/feature/5786893650231296
        // https://issues.chromium.org/issues/356601280
        return <BookingLink bookingId={row.booking_id}
            dangerouslySetInnerHTML={{__html: children}}
            style={style}
            maxWidth={meta.columnSettings.maxWidth ?? meta.columnSettings.width} />
    }
    return <BookingLink bookingId={row.booking_id}
        style={style}
        maxWidth={meta.columnSettings.maxWidth ?? meta.columnSettings.width}>{children}</BookingLink>
}


export interface DashboardOptions extends Omit<DashboardExtraOptions, 'table'> {
    ajaxSource: string,
    showPastColumn?: boolean
    serverData?: Record<string, any> | ((data: any) => Record<string, any>),
}

type DriverMaybePickerProps = BookingTableRowData & {
    driverOptions?: OptionTuples
    api: React.RefObject<DataTableApi>
}

type DriverPickerProps = BookingTableRowData & {
    driverOptions: OptionTuples
    api: React.RefObject<DataTableApi>
}

export type DriverChangeResponse = {
    segments: {
        id: number;
        number: string;
        vehicle: {
            code: string;
            name: string;
            style: {
                backgroundColor: string;
                color: string;
            };
        };
        driver: {
            id: number;
            name: string;
            status: "ASSIGNED" | "UNASSIGNED" | string; // Add other possible statuses if known
            phone: string;
        };
        sentDate: string | null; // ISO timestamp or null
        openDate: string | null; // ISO timestamp or null
        driverAlert: string | null; // Add more context if necessary
    }[];
    canDispatch: boolean;
    canEdit: boolean;
    canViewAll: boolean;
    drivers: {
        id: number;
        name: string;
        status: "ASSIGNED" | "UNASSIGNED" | string; // Add other possible statuses if known
        phone: string;
    }[];
    isSingleVehicleBooking: boolean;
    userId: number;
};

export type DriverChangeRequest = {
    segmentId: StringNum | null;
    driverId: StringNum | null;
    ignoreConflicts: boolean;
}

type DriverStatusProps = { status: string, color?: boolean }

type UnitChangeRequest = {
    segmentId: StringNum | null;
    vehicleId: StringNum | null;
    force: boolean;
}

type UnitChangeSuccessResponse = ''

// type UnitChangeConflictsResponse = UnitChangeBooking[]

function DriverStatusIconWrapped(props: DriverStatusProps) {
    return <span className={css.driverStatusIcon}><DriverStatusIcon {...props} /></span>
}

const driverStatusToColor: Record<string, string> = {
    ASSIGNED: 'var(--clr-vib-driver-assigned)',
    DISPATCHED: 'var(--clr-vib-driver-dispatched)',
    ACCEPTED: 'var(--clr-vib-driver-accepted)',
}

export function getDriverStatusColor(status: string | nil) {
    if(!status) return undefined
    return driverStatusToColor[status]
}

function DriverStatusIcon({status, color}: DriverStatusProps) {
    const iconColor = color ? driverStatusToColor[status] : undefined
    switch(status) {
        case 'ASSIGNED':
            return <Icon name={icons.ionMinusRound}
                color={iconColor}
                title="Assigned (not notified)" />
        case 'DISPATCHED':
            return <Icon name={icons.faQuestionCircle}
                color={iconColor}
                title="Dispatched (awaiting response)" />
        case 'ACCEPTED':
            return <Icon name={icons.faCheckCircle} color={iconColor} title="Accepted" />
    }
    return null
}

type VehicleMaybePickerProps = BookingTableRowData & {
    vehicleOptions?: DashboardVehicleOption[]
    api: React.RefObject<DataTableApi>
}


type VehiclePickerProps = BookingTableRowData & {
    vehicleOptions: DashboardVehicleOption[]
    api: React.RefObject<DataTableApi>
}


function VehiclePicker({
    vehicleOptions,
    vehicle,
    segment_count,
    single_vehicle_booking,
    booking_id,
    segment_id,
    api
}: VehiclePickerProps) {
    const initVehicleId = vehicle?.id || null
    const [currentVehicleId, setCurrentVehicleId] = usePropState(initVehicleId)
    const [vehicleChanging, setVehicleChanging] = useState(false)

    const currentVehicle = vehicleOptions.find(v => v.id == currentVehicleId) || vehicle


    const vehicleChangeHandler = async (ev: React.ChangeEvent<HTMLSelectElement>) => {
        // console.log(booking_id,segment_id,single_vehicle_booking,ev.currentTarget.value,segment_count)
        const newVehicleId = ev.currentTarget.value ? Number(ev.currentTarget.value) : null

        setCurrentVehicleId(newVehicleId)

        function doChangeVehicle(ignoreConflicts: boolean) {
            setVehicleChanging(true)
            postJson<UnitChangeSuccessResponse, UnitChangeRequest>(resolveRoute('api.changeScheduledVehicle', {id: booking_id}), {
                segmentId: single_vehicle_booking ? null : segment_id,
                vehicleId: newVehicleId,
                force: ignoreConflicts,
            }).finally(() => {
                // setVehicleChanging(false);
            }).then(res => {
                if(res.ok) {
                    notifySuccess("Scheduled vehicle changed")
                    api.current?.reload()
                    setVehicleChanging(false)
                } else if(res.status === 409) {
                    assumeType<typeof res & { data: BookingConflict[] }>(res)
                    appendPopup(unmount => <ConflictsDialog dontAssign={() => {
                        unmount()
                        setCurrentVehicleId(currentVehicleId)
                        setVehicleChanging(false)
                    }} assign={() => {
                        unmount()
                        doChangeVehicle(true)
                    }} conflicts={res.data} />)
                } else {
                    notifyStandardError(res, {
                        fallbackText: "Error changing vehicle",
                    })
                    setCurrentVehicleId(currentVehicleId)
                    setVehicleChanging(false)
                }
            })
        }

        doChangeVehicle(false)
    }

    const style: React.CSSProperties = {minWidth: unitWidth}
    const attrs: SelectBoxProps = {
        value: currentVehicleId ?? '',
        className: 'bookings-table-unit-select',
        onChange: vehicleChangeHandler,
        disabled: vehicleChanging,
        style,
    }
    if(currentVehicle) {
        Object.assign(style, currentVehicle.style, {borderColor: currentVehicle.style.backgroundColor})
        attrs.title = currentVehicle.name
    }

    return (
        <SelectBox {...attrs}>
            <option value=""
                style={{color: '#AEB1B3', fontStyle: 'italic', backgroundColor: 'white'}}>TBD
            </option>
            {vehicleOptions
                .filter(v => !v.isDeleted || (currentVehicle && currentVehicle.id == v.id))
                .map(v => <option key={v.id} value={v.id} style={v.style}>{v.code}</option>)
            }
        </SelectBox>
    )
}

function VehicleMaybePicker(props: VehicleMaybePickerProps) {
    const {
        vehicleOptions,
        vehicle,
        booking_id,
    } = props

    const showSelect = vehicleOptions && (!vehicle || vehicleOptions.some(v => v.id == vehicle.id))

    if(showSelect) {
        assumeDefinedProps(props, ['vehicleOptions'])
        return <VehiclePicker {...props} />
    }

    if(!vehicle) {
        return <NotSetBookingLink bookingId={booking_id} style={{minWidth: unitWidth}}>Not Set</NotSetBookingLink>
    }
    return (
        <BookingLink bookingId={booking_id} style={{...vehicle.style, minWidth: unitWidth}}>{vehicle.code}</BookingLink>
    )
}

function showDriverSelect(driver: Driver | nil, driverOptions: OptionTuples | nil) {
    return driverOptions && (!driver || driverOptions.some(d => d[0] == driver!.id))
}

function DriverPicker({
    driverOptions,
    driver,
    single_vehicle_booking,
    booking_id,
    segment_id,
    api
}: DriverPickerProps) {
    const initDriverId = driver?.id || ''
    const [currentDriverId, setCurrentDriverId] = usePropState(initDriverId)
    const [driverChanging, setDriverChanging] = useState(false)
    const [driverDispatching, setDriverDispatching] = useState(false)

    const dispatchDriver = () => {
        assert(driver?.id)
        setDriverDispatching(true)

        postJson<{ newJob: boolean }>(resolveRoute('api.dispatchDriver', {id: booking_id}), {
            segmentId: single_vehicle_booking ? null : segment_id,
            driverId: driver.id,
        }).finally(() => {
            setDriverDispatching(false)
        }).then(res => {
            if(res.ok) {
                notifySuccess(res.data.newJob ? "Driver dispatched" : "Driver updated")
                api.current?.reload()
            } else {
                notifyStandardError(res, {
                    fallbackText: "Error dispatching driver",
                })
            }
        })
    }

    const actionButtons = driver ? <>
        <ActionButtonIcon className="btn-new"
            disabled={driverDispatching}
            title={driver ? (driver.status === 'ASSIGNED' ? "Dispatch" : "Update") : "Please choose a driver"}
            icon={driver.status === 'ASSIGNED' ? icons.faPaperPlaneOutline : icons.faEnvelope}
            onClick={dispatchDriver} />
        <PhoneLink className="btn btn-new"
            disabled={!driver?.phone}
            title={driver ? (driver.phone || "Missing Phone #") : "N/A"}
            phone={driver?.phone}><Icon name={icons.faPhoneAlt} /></PhoneLink>
    </> : null

    // const driverStatus = driver ? <DriverStatusIconWrapped status={driver.status} color /> : null

    const driverChangeHandler = async (ev: React.ChangeEvent<HTMLSelectElement>) => {
        // console.log(booking_id,segment_id,single_vehicle_booking,ev.currentTarget.value,segment_count)
        const newDriverId = ev.currentTarget.value as StringNum

        if(driver && (driver.status === 'DISPATCHED' || driver.status === 'ACCEPTED')) {
            if(!await confirmDialogPromise(`This driver has already ${driver.status === 'DISPATCHED' ? 'been dispatched' : 'accepted the job'}. Are you sure want to change the driver for this booking?`)) {
                setCurrentDriverId(initDriverId)
                return
            }
        }

        setCurrentDriverId(newDriverId)

        function doChangeDriver(ignoreConflicts: boolean) {
            setDriverChanging(true)
            postJson<DriverChangeResponse, DriverChangeRequest>(resolveRoute('api.changeDriver', {id: booking_id}), {
                segmentId: single_vehicle_booking ? null : segment_id,
                driverId: newDriverId,
                ignoreConflicts,
            }).finally(() => {
                // setDriverChanging(false);
            }).then(res => {
                if(res.ok) {
                    notifySuccess("Driver changed")
                    api.current?.reload()
                    setDriverChanging(false)
                } else if(res.status === 409) {
                    assumeType<typeof res & { data: BookingConflictsResponse }>(res)
                    appendPopup(unmount => <ConflictsDialog dontAssign={() => {
                        unmount()
                        setCurrentDriverId(currentDriverId)
                        setDriverChanging(false)
                    }} assign={() => {
                        unmount()
                        doChangeDriver(true)
                    }} conflicts={res.data.conflicts} />)
                } else {
                    notifyStandardError(res, {
                        fallbackText: "Error changing driver",
                    })
                    setCurrentDriverId(currentDriverId)
                    setDriverChanging(false)
                }
            })
        }

        doChangeDriver(false)
    }

    return (
        <div className="no-pad quick-dispatch-wrap">
            {/*{driverStatus}*/}
            <SelectBox style={{minWidth: '140px'}}
                value={currentDriverId}
                onChange={driverChangeHandler}
                disabled={driverChanging}
                className={classNames(driver ? kb.driverStatusToClass(driver.status) : 'not-set', 'change-driver-select')}>
                <option key="" value="" className="not-set">— TBD —</option>
                {driverOptions!.map(opt => (
                    <option key={opt[0]} value={opt[0]}>{opt[1]}</option>
                ))}
            </SelectBox>
            {actionButtons}
        </div>
    )
}

type DriverWithIconCellProps = {
    driver: Driver | nil;
    booking_id: number;
}

export function DriverWithIconCell({driver, booking_id}: DriverWithIconCellProps) {
    if(!driver) {
        return <NotSetBookingLink bookingId={booking_id}>Not Set</NotSetBookingLink>
    }

    return (
        <BookingLink bookingId={booking_id} className={kb.driverStatusToClass(driver.status)} maxWidth="150px">
            {/*<DriverStatusIconWrapped status={driver.status} />*/}
            {driver.name}
        </BookingLink>
    )
}


function DriverMaybePicker(props: DriverMaybePickerProps) {
    const {
        driverOptions,
        driver,
        single_vehicle_booking,
        booking_id,
        segment_id,
        api
    } = props


    if(showDriverSelect(driver, driverOptions)) {
        assumeDefinedProps(props, ['driverOptions'])
        return <DriverPicker {...props} />
    }

    return <DriverWithIconCell driver={driver} booking_id={booking_id} />
}

export function BookingsDataTable(options: DashboardOptions) {
    const serverDataObj = (options.serverData || EMPTY_OBJECT) as Record<string, any>
    const dateWidth = serverDataObj.includeYear ? '100px' : '65px'
    const dateDowWidth = serverDataObj.includeYear ? '115px' : '80px'

    const columns = useMemo(() => {
        const columns: Column[] = [
            {
                title: '#',
                optionLabel: 'Booking #',
                data: 'booking_number',
                className: 'tcenter dt-booking-number',
                width: numberWidth,
                rowSpan: sameBooking,
                style: ({row}) => ({
                    backgroundColor: `var(--clr-vib-status-${row.status})`,
                }),
                render: ({row, data}) => {
                    return <BookingLink style={{color: `var(--clr-mut-status-${row.status})`}}
                        bookingId={row.booking_id}>{data}</BookingLink>
                }
            }
        ]

        if(options.showPastColumn) {
            columns.push({
                title: <abbr title="Past/Future">P/F</abbr>,
                data: 'past',
                className: 'tcenter',
                width: '60px',
                render: ({row, data}) => {
                    return <BookingLink bookingId={row.booking_id}><Icon title={data ? "Past" : "Future"}
                        name={data ? icons.ionIosClock : icons.ionIosClockOutline} /></BookingLink>
                }
            })
        }

        if(!options.timeOnly) {
            columns.push({
                title: "Date",
                data: 'date',
                className: 'tcenter',
                width: dateWidth,
                // rowSpan: sameDate,
            })

            columns.push({
                name: 'date_dow',
                title: "Date",
                optionLabel: "Date with day of the week",
                data: 'date',
                width: dateDowWidth,
                className: 'tcenter',
                render: ({row, data}) => {
                    if(!row.date) {
                        return <NotSetBookingLink bookingId={row.booking_id}>Not Set</NotSetBookingLink>
                    }
                    return <BookingLink bookingId={row.booking_id}>{row.pickup_dow} {row.date}</BookingLink>
                }
                // width: dateWidth,
                // rowSpan: sameDate,
            })
        }

        columns.push({
            title: "Time",
            data: 'time',
            className: 'tcenter',
            minWidth: '60px',
            maxWidth: '140px',
        })

        columns.push({
            title: "Unit",
            data: 'vehicle',
            width: unitWidth,
            className: 'tcenter',
            render: ({data, row}) => <VehicleMaybePicker {...row} vehicleOptions={options.vehicleOptions} api={api} />,
            style: ({data}) => data?.style,
            // rowSpan: singleVehicleBooking,
        })

        columns.push({
            title: "Customer",
            data: 'client',
            minWidth: '100px',
            // maxWidth: '150px',
            render: ({data, row}) => {
                if(!data?.name) {
                    return <NotSetBookingLink bookingId={row.booking_id}>Not Set</NotSetBookingLink>
                }
                return <BookingLink maxWidth="150px"
                    bookingId={row.booking_id}
                    title={data.name}>{data.name}</BookingLink>
            },
            // rowSpan: sameBooking,
        })

        columns.push({
            title: "Passenger",
            data: 'passenger',
            width: '110px',
            render: ({data, row}) => {
                if(!data) {
                    return <NotSetBookingLink bookingId={row.booking_id}>Not Set</NotSetBookingLink>
                }
                if(data.sameAsCustomer) {
                    return <NotSetBookingLink bookingId={row.booking_id}>Same</NotSetBookingLink>
                }
                return <BookingLink bookingId={row.booking_id} title={data.name}>{data.name}</BookingLink>
            },
            // rowSpan: sameBooking,
        })

        columns.push({
            title: "PU Place",
            data: 'pickup_place',
            className: 'dt-city-cell',
            minWidth: minPlaceWidth,
            maxWidth: maxPlaceWidth,
            orderable: false,
        })

        columns.push({
            title: "Pickup",
            data: 'pickup_city',
            className: 'dt-city-cell',
            minWidth: minCityWidth,
            maxWidth: maxCityWidth,
            orderable: false,
            // render: ({row}) => {
            //     if(!row.pickup_city) {
            //         return <NotSetLink bookingId={row.booking_id}>Not Set</NotSetLink>
            //     }
            //     return (
            //         <BookingLink bookingId={row.booking_id}>
            //             {row.pickup_place ? <div className="dt-place-name">{row.pickup_place}</div> : null}
            //             <div>{row.pickup_city}</div>
            //         </BookingLink>
            //     )
            // }
        })

        columns.push({
            title: "DO Place",
            data: 'dropoff_place',
            className: 'dt-city-cell',
            minWidth: minPlaceWidth,
            maxWidth: maxPlaceWidth,
            orderable: false,
        })


        columns.push({
            title: "Drop Off",
            data: 'dropoff_city',
            className: 'dt-city-cell',
            minWidth: minCityWidth,
            maxWidth: maxCityWidth,
            orderable: false,
        })


        if(options.showDriverColumn) {
            columns.push({
                title: "Driver",
                data: 'driver',
                minWidth: driverWidth,
                // style: ({row}) => {
                //     if(!row.driver || showDriverSelect(row.driver,options.driverOptions)) {
                //         return undefined;
                //     }
                //     return {
                //         backgroundColor: getDriverStatusColor(row.driver?.status),
                //         // color: '#fff',
                //     }
                // },
                render: ({data, row}) => <DriverMaybePicker {...row} driverOptions={options.driverOptions} api={api} />,
                // rowSpan: singleVehicleBooking,
            })
        }

        // columns.push({
        //     title: "Alert",
        //     data: 'driver_alert',
        //     className: 'tcenter',
        //     width: alertWidth,
        //     style: ({data}) => data ? ({
        //         backgroundColor: ALERT_MAP_FULL[data]?.color,
        //         color: '#fff',
        //     }) : undefined,
        //     render: ({data, row}) => {
        //         if(!data) {
        //             return <NotSetLink bookingId={row.booking_id}>Not Set</NotSetLink>
        //         }
        //         const label = ALERT_MAP_FULL[data]?.label ?? "Unknown"
        //         return <BookingLink bookingId={row.booking_id} title={label} style={{color:
        // '#fff'}}>{label}</BookingLink> }, })

        // columns.push({
        //     title: "Source",
        //     data: 'source',
        //     width: driverWidth,
        //     render: ({data, row}) => <>TODO</>,
        //     rowSpan: sameBooking,
        // })

        if(options.showPaymentsColumn) {
            columns.push({
                title: <abbr title="Payment">PT</abbr>,
                optionLabel: 'Payment',
                name: 'payment',
                className: 'tcenter',
                width: '35px',
                orderable: false,
                // style: ({row}) => ({
                //     backgroundColor: getPaymentInfo(row).cssColor
                // }),
                render: ({row}) => {
                    // return <PaymentStatusIcon {...row}/>
                    return <PaymentStatusIcon {...row} />
                },
                // rowSpan: sameBooking,
            })
        }

        return columns
    }, [dateDowWidth, dateWidth, options.driverOptions, options.showDriverColumn, options.showPastColumn, options.showPaymentsColumn, options.timeOnly, options.vehicleOptions])


    const api = useRef<DataTableApi>(null)

    const order: OrderTuple[] = [
        ['date', 'asc'],
        ['time', 'asc'],
        ['booking_number', 'asc'],
    ]

    if(options.showPastColumn) {
        order.unshift(['past', 'asc'])
    }

    const ajaxSource = options.ajaxSource
    const serverData = options.serverData

    const dataFn = useMemo<TableProps['ajax']['data']>(() => data => {
        data.filters = Array.from(filterStore.getSnapshot())
        if(serverData) {
            if(typeof serverData === 'function') {
                const ret = serverData(data)
                if(ret !== undefined) {
                    Object.assign(data, ret)
                }
            } else {
                Object.assign(data, serverData)
            }
        }
    }, [serverData])

    const ajax = useMemo<TableProps['ajax']>(() => ({
        url: ajaxSource,
        data: dataFn,
    }), [ajaxSource, dataFn])

    const config: TableProps = {
        rowKey: r => r.segment_id,
        columns,
        // rowLink: (data) => {
        //     return resolveRoute('bookings.show', {id: data.booking_id})
        // },
        ajax,
        defaultRender: DefaultBookingCellLink,
        order,
        api,
        storageKey: options.storageKey || 'bookings',
        hiddenColumns: ['passenger', 'date_dow'],
    }

    useEffect(() => {
        filterStore.subscribe(() => {
            api.current?.reload()
        })
    }, [])

    return <DataTable {...config} />
}
